import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  ContentBlock,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';
import { useEffect } from 'react';

import { Nav } from 'src/components/Nav/Nav';
import { PageWrapper } from 'src/components/PageWrapper/PageWrapper';
import type { PageProps } from 'src/types';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';
import { useQueryHistory } from 'src/utils/hooks/useQueryHistory';
import { chooseReleaseNotesPartners } from 'src/utils/partners';
import { slugify } from 'src/utils/slugify';

import translations from '../LinkYourSoftware/.vocab';

import { ReleasePartnerFilter } from './components/ReleasePartnerFilter';
import { SelectedState } from './components/SelectedState';

export const ReleaseNotes = ({ slug }: PageProps) => {
  const { t } = useTranslations(translations);
  const pageTrackingProps = {
    page: `${slugify({ str: slug })}`,
    eventName: `${slugify({ str: slug })}-viewed`,
    siteSection: 'partners',
  };

  const { value } = useQueryHistory('p');

  const { partners, selectedPartner, setSelectedPartner } =
    useIntegrationPartners();

  const releaseNotesPartners = chooseReleaseNotesPartners(partners);

  useEffect(() => {
    if (releaseNotesPartners.state === 'data') {
      setSelectedPartner(
        releaseNotesPartners.data.find((partner) => partner.slug === value),
      );
    }
  }, [setSelectedPartner, releaseNotesPartners, value]);

  return (
    <PageWrapper pageTrackingProps={pageTrackingProps} title={slug}>
      <Nav activeTab="release-notes" />

      <Box
        paddingX={['gutter', 'gutter', 'none']}
        paddingY={['xlarge', 'xlarge', 'xxlarge']}
      >
        <Stack space="xlarge" align="center">
          <ContentBlock>
            <Stack space="large" align="left">
              <Heading level="1">Release notes</Heading>
              <Text size="large">
                Select a Connected Partner from the dropdown menu to find out
                which SEEK solutions are available and the steps required to
                gain access.
              </Text>

              <Box width="full">
                <ReleasePartnerFilter
                  partners={
                    releaseNotesPartners.state === 'data'
                      ? releaseNotesPartners.data
                      : []
                  }
                  selected={selectedPartner}
                />
              </Box>
            </Stack>
          </ContentBlock>
          <ContentBlock>
            <Stack space="gutter">
              {releaseNotesPartners.state === 'error' && (
                <Alert tone="critical">
                  <Text>{t('somethingWentWrong')}</Text>
                </Alert>
              )}
              {!selectedPartner && releaseNotesPartners.state === 'data' && (
                <Alert>
                  <Text>
                    Select a provider above to see their release notes.
                  </Text>
                </Alert>
              )}
              {selectedPartner && <SelectedState partner={selectedPartner} />}
            </Stack>
          </ContentBlock>
        </Stack>
      </Box>
    </PageWrapper>
  );
};
